import React, { useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
    createFilterOptions,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';

import { Tag } from '../../types/tags.types';
import { TagsService } from '../../services/tags-service';

const TAG_COLORS = [
    '#F44336', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5',
    '#2196F3', '#03A9F4', '#00BCD4', '#009688', '#4CAF50',
    '#8BC34A', '#CDDC39', '#FFC107', '#FF9800', '#FF5722',
    '#795548', '#9E9E9E', '#607D8B', '#0D47A1', '#006064',
];

interface TagOption extends Partial<Tag> {
    inputValue?: string;
    isCreateOption?: boolean;
}

const tagFilter = createFilterOptions<TagOption>();

interface Props {
    availableTags: Tag[];
    selectedTags: Tag[];
    onChange: (tags: Tag[]) => void;
    placeholder?: string;
    inputSx?: object;
    rootSx?: object;
    primaryColor?: string;
}

export const TagAutocompleteWithCreate: React.FC<Props> = ({
    availableTags,
    selectedTags,
    onChange,
    placeholder = 'Select tags...',
    inputSx,
    rootSx,
    primaryColor = '#206FA5',
}) => {
    const [createOpen, setCreateOpen] = useState(false);
    const [newName, setNewName] = useState('');
    const [newColor, setNewColor] = useState(TAG_COLORS[0]);
    const [isCreating, setIsCreating] = useState(false);

    const openCreate = (initial: string) => {
        setNewName(initial);
        setNewColor(TAG_COLORS[0]);
        setCreateOpen(true);
    };

    const handleAutocompleteChange = (
        _: any,
        newValue: (Tag | TagOption | string)[],
    ) => {
        const last = newValue[newValue.length - 1];
        if (typeof last === 'string') {
            openCreate(last.trim());
            return;
        }
        if ((last as TagOption)?.isCreateOption) {
            openCreate((last as TagOption).inputValue?.trim() || '');
            return;
        }
        onChange(newValue as Tag[]);
    };

    const handleCreateSubmit = async () => {
        const name = newName.trim();
        if (!name) return;
        setIsCreating(true);
        try {
            const created = await TagsService.createTag(
                { name, color: newColor },
                (isValid, message) => {
                    if (!isValid) toast(message ?? 'Failed to create tag', { type: 'error' });
                },
            );
            if (created) {
                onChange([...selectedTags, created as Tag]);
                setCreateOpen(false);
                setNewName('');
            }
        } catch (err) {
            console.error('[TagAutocompleteWithCreate] create failed', err);
            toast('Failed to create tag. Please try again.', { type: 'error' });
        } finally {
            setIsCreating(false);
        }
    };

    return (
        <>
            <Autocomplete<TagOption, true, false, true>
                multiple
                freeSolo
                size="small"
                options={(availableTags || []) as TagOption[]}
                value={selectedTags as TagOption[]}
                getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    if (option.isCreateOption) return option.inputValue || '';
                    return option.name || '';
                }}
                isOptionEqualToValue={(option, value) =>
                    !!option._id && option._id === (value as TagOption)._id
                }
                filterOptions={(options, params) => {
                    const filtered = tagFilter(options, params);
                    const trimmed = params.inputValue.trim();
                    const exists = options.some(
                        (o) => o.name?.toLowerCase() === trimmed.toLowerCase(),
                    );
                    if (trimmed && !exists) {
                        filtered.push({
                            isCreateOption: true,
                            inputValue: trimmed,
                            name: `+ Add "${trimmed}" as new tag`,
                        });
                    }
                    return filtered;
                }}
                onChange={handleAutocompleteChange}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder={selectedTags.length === 0 ? placeholder : ''}
                        sx={inputSx}
                    />
                )}
                renderTags={(value, getTagProps) =>
                    value.map((tag, index) => {
                        const color = tag.color || primaryColor;
                        return (
                            <Chip
                                {...getTagProps({ index })}
                                key={tag._id}
                                label={tag.name}
                                size="small"
                                sx={{
                                    backgroundColor: `${color}22`,
                                    color,
                                    fontWeight: 500,
                                    fontSize: '12px',
                                    borderRadius: '6px',
                                    '& .MuiChip-deleteIcon': {
                                        color: `${color}99`,
                                        '&:hover': { color },
                                    },
                                }}
                            />
                        );
                    })
                }
                renderOption={(props, option) => {
                    if (option.isCreateOption) {
                        return (
                            <li {...props} key="__create__">
                                <Typography
                                    sx={{
                                        fontSize: '14px',
                                        color: primaryColor,
                                        fontWeight: 500,
                                    }}
                                >
                                    {option.name}
                                </Typography>
                            </li>
                        );
                    }
                    return (
                        <li {...props} key={option._id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Box
                                    sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: '3px',
                                        backgroundColor: option.color || '#E8F4FD',
                                        flexShrink: 0,
                                    }}
                                />
                                <Typography sx={{ fontSize: '14px' }}>{option.name}</Typography>
                            </Box>
                        </li>
                    );
                }}
                sx={{ '& .MuiAutocomplete-inputRoot': { borderRadius: '8px' }, ...rootSx }}
            />

            <Dialog open={createOpen} onClose={() => !isCreating && setCreateOpen(false)}>
                <DialogTitle>Create New Tag</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Tag Name"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        fullWidth
                        autoFocus
                        sx={{ mt: 1, mb: 3, minWidth: 320 }}
                    />
                    <Typography
                        sx={{
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#6B7A90',
                            textTransform: 'uppercase',
                            letterSpacing: '.5px',
                            mb: 1,
                        }}
                    >
                        Color
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                        {TAG_COLORS.map((color) => (
                            <Box
                                key={color}
                                onClick={() => setNewColor(color)}
                                sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: '50%',
                                    bgcolor: color,
                                    border:
                                        color === newColor
                                            ? '2px solid black'
                                            : '1px solid #ccc',
                                    cursor: 'pointer',
                                }}
                            />
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setCreateOpen(false)}
                        disabled={isCreating}
                        color="inherit"
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        variant="contained"
                        onClick={handleCreateSubmit}
                        loading={isCreating}
                        disabled={!newName.trim()}
                    >
                        Create
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    );
};
